/**
 * InstantTasks SDK bootstrap for web. Runs alongside Sentry.
 *
 * Self-gated on `VITE_IT_PROJECT_ID` + `VITE_IT_ACCESS_KEY` so unconfigured
 * environments stay no-op (no provider mounted, no network calls).
 */
import { getClient } from '@koderlabs/tasks-sdk';
import { errorsIntegration } from '@koderlabs/tasks-sdk-web-errors';
import { networkIntegration } from '@koderlabs/tasks-sdk-web-network';
import { reporterIntegration } from '@koderlabs/tasks-sdk-web-reporter';

// Canonical key per env-vars.md is `VITE_IT_PUBLIC_KEY`; legacy
// `VITE_IT_ACCESS_KEY` honored as fallback during the rename window.
const apiUrl = import.meta.env.VITE_IT_API_URL as string | undefined;
const projectId = import.meta.env.VITE_IT_PROJECT_ID as string | undefined;
const accessKey = (import.meta.env.VITE_IT_PUBLIC_KEY ??
  import.meta.env.VITE_IT_ACCESS_KEY) as string | undefined;

export const instantTasksEnabled = Boolean(apiUrl && projectId && accessKey);

export const instantTasksOptions = instantTasksEnabled
  ? {
      apiUrl,
      projectId,
      accessKey,
      release: import.meta.env.VITE_RELEASE as string | undefined,
      environment: import.meta.env.MODE,
      integrations: [
        errorsIntegration(),
        networkIntegration({
          recordRequestBody: false,
          recordResponseBody: false,
          ignoreUrls: [/\/notifications$/, /\/sdk\//],
        }),
        reporterIntegration(),
      ],
      beforeSend: (event: any) => {
        // Redact payroll + identity fields on top of built-in scrubber.
        const body = event?.request?.body;
        if (body && typeof body === 'object') {
          for (const k of ['password', 'currentPassword', 'newPassword', 'token', 'refreshToken', 'cnic', 'nationalIdentificationNumber', 'bankAccountNumber', 'basicSalary', 'grossSalary']) {
            if (k in body) body[k] = '[redacted]';
          }
        }
        return event;
      },
    }
  : null;

/**
 * Attach signed-in user to every InstantTasks event. Mirrors `setSentryUser`
 * so both backends see the same identity.
 */
export function setInstantTasksUser(
  user: { id: string; email?: string; organizationId?: string } | null,
) {
  if (!instantTasksEnabled) return;
  const client = getClient();
  if (!client) return;
  if (user) {
    client.setUser({ id: user.id, email: user.email });
    if (user.organizationId) client.setCustomData({ organizationId: user.organizationId });
  } else {
    client.setUser(null as any);
  }
}
